import Vue from "vue";
import Router from "vue-router";
import store from "../store";

// Components
import About from "@/components/About";
import Ruleset from "@/components/Ruleset";

// Achievement
import AchievementIndex from "@/components/achievement/Index";
import AchievementNew from "@/components/achievement/New";
import AchievementShow from "@/components/achievement/Show";
import AchievementEdit from "@/components/achievement/Edit";

// Character
import CharacterIndex from "@/components/character/Index";
import CharacterNew from "@/components/character/New";
import CharacterShow from "@/components/character/Show";
import CharacterSheet from "@/components/character/Sheet";
import CharacterEdit from "@/components/character/Edit";

// Page
import PageIndex from "@/components/page/Index";
import PageNew from "@/components/page/New";
import PageShow from "@/components/page/Show";
import PageEdit from "@/components/page/Edit";
import PagePublic from "@/components/page/Public";

// Item
import ItemIndex from "@/components/item/Index";
import ItemNew from "@/components/item/New";
import ItemShow from "@/components/item/Show";
import ItemEdit from "@/components/item/Edit";

// Rule
import RuleIndex from "@/components/rule/Index";
import RuleNew from "@/components/rule/New";
import RuleShow from "@/components/rule/Show";
import RuleEdit from "@/components/rule/Edit";

// Spell
import SpellIndex from "@/components/spell/Index";
import SpellNew from "@/components/spell/New";
import SpellShow from "@/components/spell/Show";
import SpellEdit from "@/components/spell/Edit";

// Reports
import ReportIndex from "@/components/report/Index";

// Session
import SignIn from "@/components/SignIn";
import Callback from "@/components/Callback";

// User
import UserIndex from "@/components/user/Index";
import UserShow from "@/components/user/Show";
import UserEdit from "@/components/user/Edit";
import UserSettings from "@/components/user/Settings";

// Error
import Error404 from "@/components/error/404";
import Error403 from "@/components/error/403";

Vue.use(Router);

// scrollBehavior:
// - only available in html5 history mode
// - defaults to no scroll behavior
// - return false to prevent scroll
const scrollBehavior = function(to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition;
  } else {
    const position = {};

    // scroll to anchor by returning the selector
    if (to.hash) {
      position.selector = to.hash;

      // specify offset of the element
      if (to.hash === "#anchor2") {
        position.offset = { y: 100 };
      }

      if (document.querySelector(to.hash)) {
        return position;
      }

      // if the returned position is falsy or an empty object,
      // will retain current scroll position.
      return false;
    }

    return new Promise(resolve => {
      // check if any matched route config has meta that requires scrolling to top
      if (to.matched.some(m => m.meta.scrollToTop)) {
        // coords will be used if no selector is provided,
        // or if the selector didn't match any element.
        position.x = 0;
        position.y = 0;
      }

      resolve(position);
    });
  }
};

const router = new Router({
  mode: "history",
  linkActiveClass: "active",
  scrollBehavior: scrollBehavior,
  routes: [
    // anonymous
    { path: "/", redirect: "/home" },
    { path: "/about", component: About },
    { path: "/ruleset", component: Ruleset },
    { path: "/sign-in", component: SignIn },
    { path: "/sign-in/callback", component: Callback },

    // characters
    { path: "/characters", component: CharacterIndex, meta: { auth: true } },
    { path: "/characters/new", component: CharacterNew, meta: { auth: true } },
    { path: "/characters/:id", component: CharacterShow, meta: { auth: true } },
    {
      path: "/characters/:id/edit",
      component: CharacterEdit,
      meta: { auth: true }
    },
    {
      path: "/characters/:id/sheet",
      component: CharacterSheet,
      name: "CharacterSheet",
      meta: { auth: true }
    },

    // pages
    { path: "/pages", component: PageIndex, meta: { auth: true } },
    { path: "/pages/new", component: PageNew, meta: { auth: "admin" } },
    { path: "/pages/:id", component: PageShow, meta: { auth: true } },
    { path: "/pages/:id/edit", component: PageEdit, meta: { auth: "admin" } },

    // items
    { path: "/items", component: ItemIndex, meta: { auth: true } },
    { path: "/items/new", component: ItemNew, meta: { auth: true } },
    { path: "/items/:id", component: ItemShow, meta: { auth: true } },
    { path: "/items/:id/edit", component: ItemEdit, meta: { auth: true } },

    // rules
    { path: "/rules", component: RuleIndex, meta: { auth: true } },
    { path: "/rules/new", component: RuleNew, meta: { auth: true } },
    { path: "/rules/:id", component: RuleShow, meta: { auth: true } },
    { path: "/rules/:id/edit", component: RuleEdit, meta: { auth: true } },

    // spells
    { path: "/spells", component: SpellIndex, meta: { auth: true } },
    { path: "/spells/new", component: SpellNew, meta: { auth: true } },
    { path: "/spells/:id", component: SpellShow, meta: { auth: true } },
    { path: "/spells/:id/edit", component: SpellEdit, meta: { auth: true } },

    // achievements
    {
      path: "/achievements",
      component: AchievementIndex,
      meta: { auth: true }
    },
    {
      path: "/achievements/new",
      component: AchievementNew,
      meta: { auth: "admin" }
    },
    {
      path: "/achievements/:id",
      component: AchievementShow,
      meta: { auth: true }
    },
    {
      path: "/achievements/:id/edit",
      component: AchievementEdit,
      meta: { auth: "admin" }
    },

    // reports
    { path: "/reports", component: ReportIndex, meta: { auth: "admin" } },

    // users
    { path: "/users", component: UserIndex, meta: { auth: "admin" } },
    { path: "/users/:id", component: UserShow, meta: { auth: true } },
    { path: "/users/:id/edit", component: UserEdit, meta: { auth: "admin" } },
    {
      path: "/users/:id/settings",
      component: UserSettings,
      meta: { auth: true }
    },

    // Errors
    { path: "/403", component: Error403 },
    { path: "/404", component: Error404 },

    // Public
    { path: "/:page", component: PagePublic },

    // We should never get here
    { path: "*", component: Error404 }
  ]
});

router.beforeEach((to, from, next) => {
  let auth = to.meta.auth;
  switch (auth) {
    case undefined: // anonymous
      if (store.getters.isAuthenticated) {
        if (
          store.state.session.user.settings.ruleset ||
          to.path === "/ruleset"
        ) {
          next();
        } else {
          next({ path: "/ruleset" });
        }
      }
      next();
      break;
    case true: // authenticated
      if (store.getters.isAuthenticated) {
        if (store.state.session.user.settings.ruleset) {
          next();
        } else {
          next({ path: "/ruleset" });
        }
      }
      break;
    default:
      // authorized
      store
        .dispatch("isAuthorized", { role: to.meta.auth })
        .then(isAuthorized => {
          if (isAuthorized) {
            next();
          } else {
            next({ path: "/403" });
          }
        })
        .catch(() => {
          next({ path: "/403" });
        });
      break;
  }
});

export default router;
